import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Check } from 'lucide-react';
import { ALLERGY_NOTE, TERMS_SUMMARY } from '@/lib/legal';

// Two mandatory checkboxes shown before payment on registration + renewal. Controlled by the parent,
// which uses `allergyChecked && termsChecked` to gate the pay button. Each label opens a popup with a
// link to the full policy page.
export function ConsentCheckboxes({ allergyChecked, termsChecked, onAllergyChange, onTermsChange }) {
  const [showAllergy, setShowAllergy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  return (
    <div className="space-y-2.5" data-testid="consent-section">
      <div className="flex items-start gap-3">
        <Checkbox
          data-testid="consent-allergy-checkbox"
          checked={allergyChecked}
          onCheckedChange={(v) => onAllergyChange(v === true)}
          className="mt-0.5"
        />
        <span className="text-sm text-charcoal leading-snug">
          I understand the{' '}
          <button
            type="button"
            data-testid="consent-allergy-link"
            onClick={() => setShowAllergy(true)}
            className="text-coriander underline underline-offset-2"
          >
            allergy notice
          </button>{' '}
          and accept the risk of cross-contamination.
        </span>
      </div>

      <div className="flex items-start gap-3">
        <Checkbox
          data-testid="consent-terms-checkbox"
          checked={termsChecked}
          onCheckedChange={(v) => onTermsChange(v === true)}
          className="mt-0.5"
        />
        <span className="text-sm text-charcoal leading-snug">
          I have read and agree to the{' '}
          <button
            type="button"
            data-testid="consent-terms-link"
            onClick={() => setShowTerms(true)}
            className="text-coriander underline underline-offset-2"
          >
            Terms &amp; Conditions
          </button>
          .
        </span>
      </div>

      {allergyChecked && termsChecked && (
        <p data-testid="consent-confirmation" className="flex items-center gap-1.5 text-xs font-medium text-coriander">
          <Check size={14} /> Thanks — you've accepted the allergy notice and Terms &amp; Conditions.
        </p>
      )}

      <Dialog open={showAllergy} onOpenChange={setShowAllergy}>
        <DialogContent data-testid="allergy-popup">
          <DialogHeader>
            <DialogTitle>Allergy Notice</DialogTitle>
            <DialogDescription>Please read and accept before ordering our food.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 text-sm text-muted-ink leading-relaxed max-h-[45vh] overflow-y-auto">
            {ALLERGY_NOTE.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
          <DialogFooter className="sm:justify-between gap-2">
            <Link
              to="/allergy"
              target="_blank"
              data-testid="allergy-popup-full-link"
              className="text-sm text-coriander underline self-center"
            >
              Read full Allergy Policy
            </Link>
            <Button data-testid="allergy-popup-close" onClick={() => setShowAllergy(false)}>
              Got it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showTerms} onOpenChange={setShowTerms}>
        <DialogContent data-testid="terms-popup">
          <DialogHeader>
            <DialogTitle>Terms &amp; Conditions</DialogTitle>
            <DialogDescription>A quick summary — the full terms are linked below.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 text-sm text-muted-ink leading-relaxed max-h-[45vh] overflow-y-auto">
            {TERMS_SUMMARY.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
          <DialogFooter className="sm:justify-between gap-2">
            <Link
              to="/terms"
              target="_blank"
              data-testid="terms-popup-full-link"
              className="text-sm text-coriander underline self-center"
            >
              Read full Terms &amp; Conditions
            </Link>
            <Button data-testid="terms-popup-close" onClick={() => setShowTerms(false)}>
              Got it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
